/**
 * Power Role - Cast Pillar
 *
 * Normaliza input sujo para número, com detecção automática de unidade.
 *
 * @example
 * import { castPower, castPowerStrict, detectPowerUnit } from '@attrx/role-morphic/power/cast';
 *
 * castPower('100 kW');                      // 100000 (converted to watts)
 * castPower('100 kW', 'kilowatt');          // 100 (kept as kilowatts)
 * castPowerStrict('100');                   // 100 (no conversion)
 * detectPowerUnit('100 hp');                // 'horsepower_mechanical'
 */

import { POWER_UNITS, POWER_ALIASES, type PowerUnit } from "./constants";
import { convertPower, POWER_BASE } from "./convert";

// =============================================================================
// TYPES
// =============================================================================

export type Result<T> = { ok: true; value: T } | { ok: false; error: string };

// =============================================================================
// CAST
// =============================================================================

/**
 * Faz cast de input para número, com conversão opcional para unidade alvo
 *
 * @param input - Valor de entrada (number, string, etc)
 * @param targetVariant - Unidade alvo (default: watt)
 * @returns Número ou null se não puder converter
 *
 * @example
 * castPower('100 kW');                 // 100000 (watts)
 * castPower('100 kW', 'kilowatt');     // 100
 * castPower('100');                    // 100
 * castPower(100);                      // 100
 * castPower('invalid');                // null
 */
export function castPower(
  input: unknown,
  targetVariant: PowerUnit = POWER_BASE,
): number | null {
  // Already a number
  if (typeof input === "number") {
    return Number.isFinite(input) ? input : null;
  }

  // String parsing
  if (typeof input === "string") {
    const parsed = parsePowerString(input);
    if (parsed === null) {
      return null;
    }

    // If unit was detected and differs from target, convert
    if (parsed.unit && parsed.unit !== targetVariant) {
      return convertPower(parsed.unit, targetVariant, parsed.value);
    }

    return parsed.value;
  }

  return null;
}

/**
 * Faz cast sem conversão (apenas parseia o número)
 *
 * @param input - Valor de entrada
 * @returns Número ou null
 *
 * @example
 * castPowerStrict('100 kW');  // 100 (ignora a unidade)
 * castPowerStrict('100');     // 100
 */
export function castPowerStrict(input: unknown): number | null {
  if (typeof input === "number") {
    return Number.isFinite(input) ? input : null;
  }

  if (typeof input === "string") {
    const parsed = parsePowerString(input);
    return parsed?.value ?? null;
  }

  return null;
}

/**
 * Versão safe de castPower que retorna Result
 */
export function tryCastPower(
  input: unknown,
  targetVariant: PowerUnit = POWER_BASE,
): Result<number> {
  const result = castPower(input, targetVariant);
  if (result === null) {
    return { ok: false, error: `Cannot cast "${String(input)}" to power` };
  }
  return { ok: true, value: result };
}

// =============================================================================
// DETECT UNIT
// =============================================================================

/**
 * Detecta a unidade de potência de uma string
 *
 * @param input - String com valor e unidade
 * @returns Unidade detectada ou null
 *
 * @example
 * detectPowerUnit('100 kW');   // 'kilowatt'
 * detectPowerUnit('100 hp');   // 'horsepower_mechanical'
 * detectPowerUnit('100');      // null
 */
export function detectPowerUnit(input: string): PowerUnit | null {
  const trimmed = input.trim().toLowerCase();

  // Extract unit part (after the number)
  const match = trimmed.match(/^[+-]?[\d.,\s]+\s*(.*)$/);
  if (!match || !match[1]) {
    return null;
  }

  const unitStr = match[1].trim();
  if (!unitStr) {
    return null;
  }

  // Check direct unit names
  if (unitStr in POWER_UNITS) {
    return unitStr as PowerUnit;
  }

  // Check aliases
  if (unitStr in POWER_ALIASES) {
    return POWER_ALIASES[unitStr] as PowerUnit;
  }

  // Check symbols (case-sensitive for some)
  for (const [unit, config] of Object.entries(POWER_UNITS)) {
    if (config.symbol.toLowerCase() === unitStr) {
      return unit as PowerUnit;
    }
  }

  return null;
}

// =============================================================================
// INTERNAL HELPERS
// =============================================================================

interface ParsedPower {
  value: number;
  unit: PowerUnit | null;
}

/**
 * Parseia string de potência extraindo valor e unidade
 */
function parsePowerString(input: string): ParsedPower | null {
  const trimmed = input.trim();
  if (!trimmed) {
    return null;
  }

  // Try to extract number and unit
  const match = trimmed.match(/^([+-]?[\d.,\s]+)\s*(.*)$/);
  if (!match) {
    return null;
  }

  const numStr = match[1].replace(/\s/g, "").replace(",", ".");
  const value = parseFloat(numStr);

  if (isNaN(value) || !Number.isFinite(value)) {
    return null;
  }

  const unitStr = match[2]?.trim().toLowerCase() || "";
  let unit: PowerUnit | null = null;

  if (unitStr) {
    // Check aliases first
    if (unitStr in POWER_ALIASES) {
      unit = POWER_ALIASES[unitStr] as PowerUnit;
    }
    // Check direct unit names
    else if (unitStr in POWER_UNITS) {
      unit = unitStr as PowerUnit;
    }
    // Check symbols
    else {
      for (const [u, config] of Object.entries(POWER_UNITS)) {
        if (config.symbol.toLowerCase() === unitStr) {
          unit = u as PowerUnit;
          break;
        }
      }
    }
  }

  return { value, unit };
}
